import api from "../../../lib/axios";
import type { BasePagingResponse, BaseResponse } from "../../../lib/api-contract";
import { apiUtils } from "../../../utils/apiUtils";
import type {
  CreateEventForAdminRequest,
  EventDetailForAdminResponse,
  EventImportItem,
  EventImportResponse,
  EventImportValidationResponse,
  EventItemResponse,
  EventListForAdminResponse,
  EventStatisticsResponse,
  GetEventListForAdminRequest,
  UpdateEventForAdminRequest,
} from "../types/event.types";

const appendFormText = (formData: FormData, key: string, value: string | null | undefined) => {
  if (value !== undefined && value !== null && value !== "") {
    formData.append(key, value);
  }
};

const appendFormNumber = (formData: FormData, key: string, value: number | null | undefined) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    formData.append(key, String(value));
  }
};

const appendFormIds = (formData: FormData, key: string, values: string[] | undefined) => {
  if (!values) return;

  for (const value of values) {
    if (value !== "") {
      formData.append(key, value);
    }
  }
};

const appendEventForm = (
  formData: FormData,
  request: CreateEventForAdminRequest | UpdateEventForAdminRequest,
) => {
  formData.append("title", request.title);
  formData.append("event_date", request.event_date);
  formData.append("start_time", request.start_time);
  formData.append("end_time", request.end_time);
  formData.append("timezone", request.timezone);

  appendFormText(formData, "description", request.description);
  appendFormText(formData, "location", request.location);
  appendFormNumber(formData, "latitude", request.latitude);
  appendFormNumber(formData, "longitude", request.longitude);
  appendFormNumber(formData, "radius", request.radius);
  appendFormText(formData, "check_in_time", request.check_in_time);
  appendFormText(formData, "guest_attendance_verification", request.guest_attendance_verification);
  appendFormNumber(formData, "points", request.points);
  appendFormText(formData, "image_url", request.image_url);
  appendFormText(formData, "neighborhood_id", request.neighborhood_id);
  appendFormText(formData, "category_id", request.category_id);

  if (request.event_image_file) {
    formData.append("event_image_file", request.event_image_file);
  }
};

export async function getEventList(
  request: GetEventListForAdminRequest,
  signal?: AbortSignal,
): Promise<BasePagingResponse<EventListForAdminResponse>> {
  return apiUtils.getData<BasePagingResponse<EventListForAdminResponse>>(
    "/v1/portal/event",
    "Failed to fetch event list",
    {
      params: request,
      paramsSerializer: { indexes: null },
      signal,
    },
  );
}

export function getEventStatistics(signal?: AbortSignal): Promise<EventStatisticsResponse> {
  return apiUtils.getData<EventStatisticsResponse>(
    "/v1/portal/event/statistic",
    "Failed to fetch event statistics",
    { signal },
  );
}

export function getEventDetail(
  id: string,
  signal?: AbortSignal,
): Promise<EventDetailForAdminResponse> {
  return apiUtils.getData<EventDetailForAdminResponse>(
    `/v1/portal/event/${id}`,
    "Failed to fetch event detail",
    { signal },
  );
}

export async function getEventQrImage(id: string, signal?: AbortSignal): Promise<Blob> {
  const response = await api.get(`/v1/portal/event/${id}/qr-image`, {
    responseType: "blob",
    signal,
  });

  return response.data as Blob;
}

export async function createEvent(
  request: CreateEventForAdminRequest,
  signal?: AbortSignal,
): Promise<EventItemResponse> {
  const formData = new FormData();
  appendEventForm(formData, request);
  appendFormIds(formData, "sub_category_ids", request.sub_category_ids);

  return apiUtils.unwrap(
    await apiUtils.postWithFormData<EventItemResponse>("/v1/portal/event", formData, { signal }),
    "Failed to create event",
  );
}

export async function updateEvent(
  id: string,
  request: UpdateEventForAdminRequest,
  signal?: AbortSignal,
): Promise<EventItemResponse> {
  const formData = new FormData();
  appendEventForm(formData, request);
  appendFormIds(formData, "interest_ids", request.interest_ids);
  appendFormIds(formData, "sub_category_ids", request.sub_category_ids);

  return apiUtils.unwrap(
    await apiUtils.putWithFormData<EventItemResponse>(`/v1/portal/event/${id}`, formData, {
      signal,
    }),
    "Failed to update event",
  );
}

export function validateImportEvents(
  events: EventImportItem[],
): Promise<EventImportValidationResponse> {
  return apiUtils.postData<EventImportValidationResponse>(
    "/v1/portal/event/import/validate",
    { events },
    "Failed to validate event import",
  );
}

export function importEvents(events: EventImportItem[]): Promise<EventImportResponse> {
  return apiUtils.postData<EventImportResponse>(
    "/v1/portal/event/import",
    { events },
    "Failed to import events",
  );
}

export function deleteEvent(
  id: string,
  signal?: AbortSignal,
): Promise<BaseResponse<object | null>> {
  return apiUtils.delete<object | null>(`/v1/portal/event/${id}`, { signal });
}
